import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  DocumentDuplicateIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import { TemplateDialog } from "../templates/template-dialog";
import { createTextDocument } from "../../../api/textDocuments";

/**
 * Dialog to create a new text document. Lets the user set a title and optionally pick an
 * existing template (its content seeds the document body on the server) before creation.
 * On success calls +onCreated(document)+ so the caller can navigate to the editor.
 */
export default function NewTextDocumentDialog({
  open,
  onClose,
  subjectType,
  subjectId,
  onCreated,
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [template, setTemplate] = useState(null);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const reset = () => {
    setTitle("");
    setTemplate(null);
    setTemplateOpen(false);
    setIsSaving(false);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleSelectTemplate = (selected) => {
    setTemplate(selected);
    setTemplateOpen(false);
    if (!title.trim() && selected?.title) {
      setTitle(selected.title);
    }
  };

  const handleCreateDialogClose = () => {
    if (templateOpen) return;
    handleClose();
  };

  const handleCreate = async (event) => {
    event?.preventDefault?.();
    const finalTitle = title.trim() || t("textDocuments.untitled");
    setIsSaving(true);
    const payload = { title: finalTitle };
    if (template?.id) payload.template_id = template.id;
    if (subjectType && subjectId != null) {
      payload.subject_type = subjectType;
      payload.subject_id = subjectId;
    }
    const res = await createTextDocument(payload);
    setIsSaving(false);
    if (res?.id) {
      onCreated?.(res);
      reset();
    } else {
      toast.error(t("textDocuments.save_failed"));
    }
  };

  return (
    <>
      <PMDialog
        open={open}
        onClose={handleCreateDialogClose}
        title={t("textDocuments.new")}
        size="lg"
        closeOnOutsideClick={!templateOpen}
      >
        <form onSubmit={handleCreate} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700">
              {t("Title")}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              placeholder={t("textDocuments.title_placeholder")}
              className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">
              {t("textDocuments.template")}
            </label>
            <p className="mt-0.5 text-xs text-slate-500">
              {t("textDocuments.template_hint")}
            </p>
            {template ? (
              <div className="mt-2 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <span className="flex min-w-0 items-center gap-2 text-sm text-slate-800">
                  <DocumentDuplicateIcon className="h-4 w-4 flex-shrink-0 text-slate-400" />
                  <span className="truncate">{template.title}</span>
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setTemplateOpen(true)}
                    className="text-xs font-semibold text-slate-600 hover:text-slate-900"
                  >
                    {t("textDocuments.change_template")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setTemplate(null)}
                    className="rounded-full p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                    title={t("textDocuments.remove_template")}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setTemplateOpen(true)}
                className="mt-2 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <DocumentDuplicateIcon className="h-4 w-4" />
                {t("textDocuments.choose_template")}
              </button>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
            >
              {isSaving ? t("Saving...") : t("textDocuments.create")}
            </button>
          </div>
        </form>
      </PMDialog>

      <TemplateDialog
        open={templateOpen}
        onClose={() => setTemplateOpen(false)}
        onChange={(selected) => handleSelectTemplate(selected)}
        zIndex={3100}
      />
    </>
  );
}
